import { useCallback, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { getAllAudioAssets } from '@dyl/apiClients/backendApi';
import createDatabaseManager from '@dyl/utils/createDatabaseManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const { Provider: AudiosManager, use: useRaw } = createDatabaseManager({
    requestHook: function useAudiosRequest() {
        const { enqueueSnackbar } = useSnackbar();

        return useCallback(async () => {
            while (true) {
                try {
                    return await getAllAudioAssets({ type: 'ITEM', version: '0' });
                }
                catch {
                    await new Promise(resolve => enqueueSnackbar( 'Failed to load audios', {
                        variant: 'error',
                        persist: true,
                        action: createSnackbarAction({
                            iconComponent: RefreshIcon,
                            onClick: resolve,
                        }),
                    }));
                }
            }
        }, []);
    },
    defaultValue: [],
    contextName: 'AudiosManager',
});

// Chose to replicate the AudiosManager as the backend won't provide all audios together
// And we'd prefer to persist the item audios for all task pages to function seamlessly
const { Provider: MotivationalAudiosManager, use: useMotivationalRaw } = createDatabaseManager({
    requestHook: function useAudiosRequest() {
        const { enqueueSnackbar } = useSnackbar();

        return useCallback(async (args) => {
            while (true) {
                try {
                    return await getAllAudioAssets('MOTIVATIONAL');
                }
                catch {
                    await new Promise(resolve => enqueueSnackbar( 'Failed to load audios', {
                        variant: 'error',
                        persist: true,
                        action: createSnackbarAction({
                            iconComponent: RefreshIcon,
                            onClick: resolve,
                        }),
                    }));
                }
            }
        }, []);
    },
    defaultValue: [],
    contextName: 'MotivationalAudiosManager',
});

export { AudiosManager, MotivationalAudiosManager };

export function useAudios(type = 'ITEM') {
    const {
        data: audios,
        status: audiosStatus,
        refresh: refreshAudios,
    } = useRaw();

    const {
        data: audiosMotivational,
        status: audiosMotivationalStatus,
        refresh: refreshMotivationalAudios,
    } = useMotivationalRaw();

    useEffect(() => {
        if (audiosStatus !== 'complete') {
            refreshAudios();
        }

        if (audiosMotivationalStatus !== 'complete') {
            refreshMotivationalAudios();
        }
    }, []);

    return type === 'ITEM' ? {
        audios,
        audiosStatus,
        refreshAudios,
    } : {
        audios: audiosMotivational,
        audiosStatus: audiosMotivationalStatus,
        refreshAudios: refreshMotivationalAudios,
    };
}

export function useAudio(audioId) {
    const { audios, audiosStatus } = useAudios();

    return useMemo(() => {
        if (audiosStatus !== 'complete') return null;
        if (!audioId) return null;

        const audio = audios.find(a => a.audio_id === audioId);

        return audio ?? null;
    }, [audioId, audios, audiosStatus]);
}